import { Shield, Download, TrendingUp, Target, AlertTriangle, Activity, Clock, BarChart3, LineChart, Lock, Copy, Check, Zap, Eye, Server, Globe, Fingerprint as FpIcon, Info, ExternalLink } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { useState, useEffect, useMemo } from 'react';
import { LineChart as RechartsLine, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useRecommendedActions, useDeceptions, useThreatFeed, useAttackerProfiles } from '../lib/hooks';
import {
  ENGAGEMENT_VOCAB,
  isDeepCallback,
  resolveDeepCallbacksDaily,
  resolveTimeBurnedDaily,
  isTimeBurned,
  timeBurnRate,
  callbackRate,
} from '../lib/engagement-vocab';

export function ReportsEnhanced() {
  const { api, isConnected } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'intel' | 'honeypots' | 'techniques'>('overview');
  const [timeRange, setTimeRange] = useState<7 | 30 | 90 | 0>(30);
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState<any>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Fetch recommended actions for threat intel tab
  const { data: actions, isLoading: actionsLoading } = useRecommendedActions();
  const { data: deceptionsData } = useDeceptions();
  const { data: threatFeed } = useThreatFeed();
  const { data: attackerProfiles } = useAttackerProfiles(70, 20);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Honeypot effectiveness — P0-1: time burned ≠ deep callbacks
  const honeypotStats = useMemo(() => {
    if (!deceptionsData?.deceptions) return [];

    const stats = deceptionsData.deceptions.reduce((acc, d) => {
      const variant = d.variant || 'unknown';
      if (!acc[variant]) {
        acc[variant] = { served: 0, timeBurned: 0, legacyEngaged: 0, timeWasted: 0 };
      }
      acc[variant].served++;
      if (isTimeBurned(d.time_wasted_ms)) acc[variant].timeBurned++;
      // LEGACY per-variant flag only. The hop table is not joinable to a
      // deception variant from this list API, so per-variant deep callbacks
      // cannot be computed correctly here — surfaced as 'legacy engaged'.
      if (isDeepCallback(d.engaged)) acc[variant].legacyEngaged++;
      acc[variant].timeWasted += d.time_wasted_ms || 0;
      return acc;
    }, {} as Record<string, { served: number; timeBurned: number; legacyEngaged: number; timeWasted: number }>);

    return Object.entries(stats)
      .map(([variant, data]) => ({
        variant,
        ...data,
        /** Prefer time-burn rate as primary effectiveness signal */
        timeBurnRate: timeBurnRate(data.timeBurned, data.served),
        legacyEngagedRate: callbackRate(data.legacyEngaged, data.served),
        avgTimeWasted: data.timeBurned > 0 ? data.timeWasted / data.timeBurned : 0,
      }))
      .sort((a, b) => b.timeBurnRate - a.timeBurnRate);
  }, [deceptionsData]);

  useEffect(() => {
    if (!api) return;
    
    const fetchTrends = async () => {
      setLoading(true);
      try {
        const data = await api.getHistoricalTrends(timeRange);
        
        // Pad data to show full time range even if no data exists for some days
        if (data && timeRange > 0) {
          const paddedData = { ...data };
          const existingDates = new Set(data.dailyStats.map((d: any) => d.date));
          const today = new Date();
          const allDays = [];
          
          // Generate all dates in range
          for (let i = timeRange - 1; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            
            if (existingDates.has(dateStr)) {
              allDays.push(data.dailyStats.find((d: any) => d.date === dateStr));
            } else {
              allDays.push({
                date: dateStr,
                sessions: 0,
                threats: 0,
                avg_confidence: 0
              });
            }
          }
          
          paddedData.dailyStats = allDays;
          
          // Pad deceptionTrends and threatLevelTrends similarly
          const existingDeceptionDates = new Set(data.deceptionTrends.map((d: any) => d.date));
          const existingThreatLevelDates = new Set(data.threatLevelTrends.map((d: any) => d.date));
          
          const allDeceptionDays = [];
          const allThreatLevelDays = [];
          
          for (let i = timeRange - 1; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            
            if (existingDeceptionDates.has(dateStr)) {
              allDeceptionDays.push(data.deceptionTrends.find((d: any) => d.date === dateStr));
            } else {
              allDeceptionDays.push({
                date: dateStr,
                served: 0,
                time_burned: 0,
                engaged: 0,
                deep_callbacks: 0,
                time_wasted: 0
              });
            }
            
            if (existingThreatLevelDates.has(dateStr)) {
              allThreatLevelDays.push(data.threatLevelTrends.find((d: any) => d.date === dateStr));
            } else {
              allThreatLevelDays.push({
                date: dateStr,
                none: 0,
                low: 0,
                medium: 0,
                high: 0,
                critical: 0
              });
            }
          }
          
          paddedData.deceptionTrends = allDeceptionDays;
          paddedData.threatLevelTrends = allThreatLevelDays;
          
          setTrends(paddedData);
        } else {
          setTrends(data);
        }
      } catch (err) {
        console.error('Failed to load trends:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrends();
  }, [api, timeRange]);

  if (!isConnected) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold text-white mb-4">Reports & Intelligence</h2>
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Connect to the control plane to view reports</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Reports & Intelligence</h2>
          <p className="text-gray-400 mt-1">Comprehensive security analytics and actionable insights</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-sg-card border border-sg-border rounded-lg p-1">
            <button
              onClick={() => setTimeRange(7)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                timeRange === 7 ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setTimeRange(30)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                timeRange === 30 ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              30 Days
            </button>
            <button
              onClick={() => setTimeRange(90)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                timeRange === 90 ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              90 Days
            </button>
            <button
              onClick={() => setTimeRange(0)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                timeRange === 0 ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              All Time
            </button>
          </div>
          <button 
            onClick={() => alert('PDF export coming soon! Data can be exported via API.')}
            className="flex items-center gap-2 px-4 py-2 bg-sg-accent text-white rounded-lg text-sm font-medium hover:bg-sg-accent/90 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 border-b border-sg-border">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 font-medium transition-all relative ${
            activeTab === 'overview'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </div>
        </button>
        <button
          onClick={() => setActiveTab('intel')}
          className={`px-6 py-3 font-medium transition-all relative ${
            activeTab === 'intel'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Threat Intelligence
          </div>
        </button>
        <button
          onClick={() => setActiveTab('honeypots')}
          className={`px-6 py-3 font-medium transition-all relative ${
            activeTab === 'honeypots'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Honeypot Performance
          </div>
        </button>
        <button
          onClick={() => setActiveTab('techniques')}
          className={`px-6 py-3 font-medium transition-all relative ${
            activeTab === 'techniques'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Attack Techniques
          </div>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 mt-4">Loading analytics...</p>
        </div>
      ) : !trends ? (
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">No data available for this time period</p>
        </div>
      ) : (
        <>
          {/* TAB 1: OVERVIEW - Historical Analytics */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    <div className="text-2xl font-bold text-white">
                      {trends.dailyStats.reduce((sum: number, d: any) => sum + d.sessions, 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">Total Sessions</div>
                  <div className="text-xs text-cyan-400 mt-1">
                    {timeRange > 0 
                      ? `${Math.round(trends.dailyStats.reduce((sum: number, d: any) => sum + d.sessions, 0) / Math.max(1, trends.dailyStats.length))} per day avg`
                      : `Across ${trends.dailyStats.length} days`}
                  </div>
                </div>

                <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <div className="text-2xl font-bold text-white">
                      {trends.dailyStats.reduce((sum: number, d: any) => sum + d.threats, 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">Threats Detected</div>
                  <div className="text-xs text-red-400 mt-1">
                    {trends.dailyStats.length > 0 
                      ? ((trends.dailyStats.reduce((sum: number, d: any) => sum + d.threats, 0) / 
                          trends.dailyStats.reduce((sum: number, d: any) => sum + d.sessions, 0)) * 100).toFixed(1)
                      : 0}% of traffic
                  </div>
                </div>

                <div
                  className="bg-sg-card border border-sg-border rounded-xl p-6"
                  title={ENGAGEMENT_VOCAB.timeBurned.hint}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Target className="w-5 h-5 text-green-400" />
                    <div className="text-2xl font-bold text-white">
                      {trends.deceptionTrends.reduce((sum: number, d: any) => sum + resolveTimeBurnedDaily(d), 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">{ENGAGEMENT_VOCAB.timeBurned.label}</div>
                  <div className="text-xs text-green-400 mt-1">
                    {trends.deceptionTrends.length > 0
                      ? timeBurnRate(
                          trends.deceptionTrends.reduce((sum: number, d: any) => sum + resolveTimeBurnedDaily(d), 0),
                          trends.deceptionTrends.reduce((sum: number, d: any) => sum + (d.served || 0), 0),
                        ).toFixed(1)
                      : 0}% of decoys applied delay
                  </div>
                </div>

                <div
                  className="bg-sg-card border border-sg-border rounded-xl p-6"
                  title={ENGAGEMENT_VOCAB.deepCallbacks.hint}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Target className="w-5 h-5 text-purple-400" />
                    <div className="text-2xl font-bold text-white">
                      {trends.deceptionTrends.reduce((sum: number, d: any) => sum + resolveDeepCallbacksDaily(d), 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">{ENGAGEMENT_VOCAB.deepCallbacks.label}</div>
                  <div className="text-xs text-purple-400 mt-1">
                    attackers followed a decoy onward
                  </div>
                </div>

                <div
                  className="bg-sg-card border border-sg-border rounded-xl p-6"
                  title={ENGAGEMENT_VOCAB.timeWasted.hint}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-5 h-5 text-amber-400" />
                    <div className="text-2xl font-bold text-white">
                      {Math.round(trends.deceptionTrends.reduce((sum: number, d: any) => sum + d.time_wasted, 0) / 1000 / 60)}m
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">{ENGAGEMENT_VOCAB.timeWasted.label}</div>
                  <div className="text-xs text-amber-400 mt-1">
                    {Math.round(trends.deceptionTrends.reduce((sum: number, d: any) => sum + d.time_wasted, 0) / 
                      Math.max(1, trends.deceptionTrends.reduce((sum: number, d: any) => sum + resolveTimeBurnedDaily(d), 0)) / 1000)}s avg per time-burned decoy
                  </div>
                </div>
              </div>

              {/* Activity Timeline */}
              <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <LineChart className="w-5 h-5 text-cyan-400" />
                  Activity Timeline
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trends.dailyStats}>
                    <defs>
                      <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorThreats" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f87171" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f87171" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#9ca3af"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                    />
                    <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="sessions" 
                      stroke="#06b6d4" 
                      fillOpacity={1} 
                      fill="url(#colorSessions)"
                      name="Sessions"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="threats" 
                      stroke="#f87171" 
                      fillOpacity={1} 
                      fill="url(#colorThreats)"
                      name="Threats"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Threat Level Distribution */}
              <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-orange-400" />
                  Threat Level Distribution Over Time
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={trends.threatLevelTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#9ca3af"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                    />
                    <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Bar dataKey="critical" stackId="a" fill="#dc2626" name="Critical" />
                    <Bar dataKey="high" stackId="a" fill="#f97316" name="High" />
                    <Bar dataKey="medium" stackId="a" fill="#eab308" name="Medium" />
                    <Bar dataKey="low" stackId="a" fill="#22c55e" name="Low" />
                    <Bar dataKey="none" stackId="a" fill="#6b7280" name="None" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-2 gap-8">
                {/* Top Attackers */}
                <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Most Persistent Attackers
                  </h3>
                  <div className="space-y-3">
                    {trends.topAttackers.slice(0, 10).map((attacker: any, idx: number) => (
                      <div key={attacker.source_ip} className="flex items-center justify-between p-3 bg-sg-dark rounded-lg border border-sg-border">
                        <div className="flex items-center gap-3">
                          <div className="text-sm font-mono text-gray-500">#{idx + 1}</div>
                          <div>
                            <div className="text-sm font-mono text-white">{attacker.source_ip}</div>
                            <div className="text-xs text-gray-500">{attacker.country || 'Unknown'}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-white">{attacker.session_count} sessions</div>
                          <div className={`text-xs ${
                            attacker.max_threat_level === 'critical' ? 'text-red-400' :
                            attacker.max_threat_level === 'high' ? 'text-orange-400' :
                            'text-yellow-400'
                          }`}>
                            {attacker.max_confidence}% confidence
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Techniques */}
                <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-purple-400" />
                    Most Common Attack Techniques
                  </h3>
                  <div className="space-y-3">
                    {trends.topTechniques.slice(0, 10).map((tech: any, idx: number) => (
                      <div key={tech.technique} className="flex items-center justify-between p-3 bg-sg-dark rounded-lg border border-sg-border">
                        <div className="flex items-center gap-3">
                          <div className="text-sm font-mono text-gray-500">#{idx + 1}</div>
                          <div className="text-sm text-white">{tech.technique}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold text-white">{tech.count}</div>
                          <div className="h-2 bg-gray-700 rounded-full" style={{ width: '80px' }}>
                            <div 
                              className="h-full bg-purple-500 rounded-full" 
                              style={{ 
                                width: `${Math.min(100, (tech.count / trends.topTechniques[0].count) * 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Deception Effectiveness */}
              <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-400" />
                  Deception Effectiveness Trends
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsLine data={trends.deceptionTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#9ca3af"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                    />
                    <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="served" stroke="#06b6d4" name={ENGAGEMENT_VOCAB.decoysServed.label} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey={(d: any) => resolveTimeBurnedDaily(d)} stroke="#22c55e" name={ENGAGEMENT_VOCAB.timeBurned.label} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey={(d: any) => resolveDeepCallbacksDaily(d)} stroke="#a855f7" name={ENGAGEMENT_VOCAB.deepCallbacks.label} strokeWidth={2} dot={false} />
                  </RechartsLine>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* TAB 2: THREAT INTELLIGENCE - Actionable Recommendations */}
          {activeTab === 'intel' && (
            <div className="space-y-8">
              {/* Critical Findings Alert */}
              {actions && actions.topIpsToBlock.length > 0 && (
                <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-6 h-6 text-red-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-2">Critical Threat Alert</h3>
                      <p className="text-sm text-gray-300 mb-4">
                        {actions.topIpsToBlock.length} high-confidence threat{actions.topIpsToBlock.length > 1 ? 's' : ''} detected requiring immediate attention. 
                        These IPs have shown malicious behavior with {actions.topIpsToBlock[0].confidence}%+ confidence.
                      </p>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                          <span className="text-red-300">Active Threats: {actions.summary.totalThreatsBlocked}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Zap className="w-3 h-3 text-orange-400" />
                          <span className="text-orange-300">Techniques: {actions.summary.activeTechniques}</span>
                        </div>
                        {threatFeed?.threats && (
                          <div className="flex items-center gap-2">
                            <Activity className="w-3 h-3 text-yellow-400" />
                            <span className="text-yellow-300">Live Feed: {threatFeed.threats.filter(t => t.level === 'high' || t.level === 'critical').length} active</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <div className="text-2xl font-bold text-white">{actions?.summary.totalThreatsBlocked || 0}</div>
                  </div>
                  <div className="text-sm text-gray-400">High-Priority IPs to Block</div>
                  <div className="text-xs text-red-400 mt-1">Immediate action recommended</div>
                </div>

                <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Zap className="w-5 h-5 text-orange-400" />
                    <div className="text-2xl font-bold text-white">{actions?.summary.activeTechniques || 0}</div>
                  </div>
                  <div className="text-sm text-gray-400">Active Attack Techniques</div>
                  <div className="text-xs text-orange-400 mt-1">MITRE ATT&CK mapped</div>
                </div>
              </div>

              {/* Recommended Actions */}
              <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-cyan-400" />
                    Recommended Actions
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">Immediate response intelligence based on live threat analysis</p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {/* Priority IPs to Block */}
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-red-400" />
                      Priority IPs to Block
                    </h4>
                    {actionsLoading ? (
                      <div className="text-sm text-gray-500">Loading...</div>
                    ) : !actions || actions.topIpsToBlock.length === 0 ? (
                      <div className="text-sm text-gray-500 bg-sg-dark rounded-lg p-4 border border-sg-border">
                        No high-priority threats detected. All traffic is below blocking thresholds.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {actions.topIpsToBlock.slice(0, 5).map((ip) => (
                          <div key={ip.ip} className="bg-sg-dark rounded-lg p-3 border border-sg-border hover:border-red-500/30 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-mono text-white">{ip.ip}</span>
                                  <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">{ip.confidence}%</span>
                                  {ip.country && (
                                    <span className="text-xs text-gray-500">{ip.city}, {ip.country}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                  <span>{ip.sessions} sessions</span>
                                  {ip.techniques.length > 0 && (
                                    <>
                                      <span>•</span>
                                      <span className="text-orange-400">{ip.techniques.slice(0, 2).join(', ')}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => copyToClipboard(ip.ip, ip.ip)}
                                className="p-2 hover:bg-sg-card rounded transition-colors"
                                title="Copy IP"
                              >
                                {copiedId === ip.ip ? (
                                  <Check className="w-4 h-4 text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Firewall Rules */}
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-cyan-400" />
                      Copy-Paste Firewall Rules
                    </h4>
                    {!actions || actions.firewallRules.length === 0 ? (
                      <div className="text-sm text-gray-500 bg-sg-dark rounded-lg p-4 border border-sg-border">
                        No firewall rules needed at this time.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {actions.firewallRules.slice(0, 3).map((rule) => (
                          <div key={rule.ip} className="bg-sg-dark rounded-lg p-3 border border-sg-border">
                            <div className="flex items-center justify-between mb-2">
                              <code className="text-xs font-mono text-white">{rule.ip}</code>
                              <span className="text-xs text-gray-500">{rule.reason}</span>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs font-mono text-cyan-400 bg-black/30 px-2 py-1 rounded">
                                  {rule.rule}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(rule.rule, `rule-${rule.ip}`)}
                                  className="p-1.5 hover:bg-sg-card rounded transition-colors"
                                  title="Copy iptables rule"
                                >
                                  {copiedId === `rule-${rule.ip}` ? (
                                    <Check className="w-3.5 h-3.5 text-green-400" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5 text-gray-400" />
                                  )}
                                </button>
                              </div>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs font-mono text-purple-400 bg-black/30 px-2 py-1 rounded">
                                  {rule.cloudflareRule}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(rule.cloudflareRule, `cf-${rule.ip}`)}
                                  className="p-1.5 hover:bg-sg-card rounded transition-colors"
                                  title="Copy Cloudflare rule"
                                >
                                  {copiedId === `cf-${rule.ip}` ? (
                                    <Check className="w-3.5 h-3.5 text-green-400" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5 text-gray-400" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Active Attack Techniques */}
                {actions && actions.criticalTechniques.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-sg-border">
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-400" />
                      Active Attack Techniques (MITRE ATT&CK)
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {actions.criticalTechniques.slice(0, 8).map((tech) => (
                        <div key={tech.technique} className="bg-sg-dark rounded-lg p-3 border border-orange-500/20">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-white truncate">{tech.technique}</div>
                              <div className="text-xs text-gray-400">{tech.occurrences} occurrences</div>
                            </div>
                            <div className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded font-semibold ml-2">
                              {tech.occurrences}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Advanced Threat Intelligence */}
              {attackerProfiles && attackerProfiles.profiles.length > 0 && (
                <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <FpIcon className="w-5 h-5 text-purple-400" />
                      Threat Actor Profiles
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">Correlated attacker behavior patterns and infrastructure analysis</p>
                  </div>

                  <div className="space-y-4">
                    {attackerProfiles.profiles.slice(0, 5).map((profile) => (
                      <div key={profile.id} className="bg-sg-dark rounded-lg p-4 border border-sg-border hover:border-purple-500/30 transition-colors">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              profile.automation_detected ? 'bg-red-500/20' : 'bg-blue-500/20'
                            }`}>
                              {profile.automation_detected ? (
                                <Server className="w-5 h-5 text-red-400" />
                              ) : (
                                <Globe className="w-5 h-5 text-blue-400" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-white">
                                  {profile.automation_detected ? 'Automated Attack Campaign' : 'Human-Operated Threat'}
                                </span>
                                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded font-semibold">
                                  {profile.confidence}% confidence
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {profile.session_count} sessions • {profile.source_countries[0] || 'Unknown location'} • First seen {new Date(profile.first_seen).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-sm font-semibold ${
                              profile.sophistication_score >= 80 ? 'text-red-400' :
                              profile.sophistication_score >= 60 ? 'text-orange-400' :
                              profile.sophistication_score >= 40 ? 'text-yellow-400' : 'text-gray-400'
                            }`}>
                              {profile.sophistication_score}/100
                            </div>
                            <div className="text-xs text-gray-500">Sophistication</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-3 mb-3">
                          <div className="bg-sg-card rounded p-2">
                            <div className="text-xs text-gray-500">Tools</div>
                            <div className="text-sm font-semibold text-white">{profile.tools_detected.length || 'None'}</div>
                          </div>
                          <div className="bg-sg-card rounded p-2">
                            <div className="text-xs text-gray-500">Techniques</div>
                            <div className="text-sm font-semibold text-white">{profile.techniques_used.length}</div>
                          </div>
                          <div className="bg-sg-card rounded p-2">
                            <div className="text-xs text-gray-500">IPs</div>
                            <div className="text-sm font-semibold text-white">{profile.source_ips.length}</div>
                          </div>
                          <div className="bg-sg-card rounded p-2">
                            <div className="text-xs text-gray-500">Time Wasted</div>
                            <div className="text-sm font-semibold text-green-400">{Math.round(profile.time_wasted_total_ms / 1000)}s</div>
                          </div>
                        </div>

                        {profile.tools_detected.length > 0 && (
                          <div className="mb-2">
                            <div className="text-xs text-gray-500 mb-1">Detected Tools:</div>
                            <div className="flex flex-wrap gap-1">
                              {profile.tools_detected.slice(0, 5).map((tool) => (
                                <span key={tool} className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded font-mono">
                                  {tool}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {profile.attack_phases.length > 0 && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-gray-500">Attack Chain:</span>
                            {profile.attack_phases.map((phase, idx) => (
                              <span key={idx}>
                                <span className="text-orange-400 capitalize">{phase}</span>
                                {idx < profile.attack_phases.length - 1 && <span className="text-gray-600 mx-1">→</span>}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Live Threat Feed Summary */}
              {threatFeed && threatFeed.threats.length > 0 && (
                <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Activity className="w-5 h-5 text-cyan-400" />
                      Live Threat Intelligence
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">Real-time attribution and infrastructure analysis</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {threatFeed.threats.filter(t => t.level === 'high' || t.level === 'critical').slice(0, 6).map((threat) => (
                      <div key={threat.id} className="bg-sg-dark rounded-lg p-4 border border-sg-border">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <code className="text-sm font-mono text-white">{threat.sourceIp}</code>
                              <span className={`px-2 py-0.5 text-xs rounded font-semibold ${
                                threat.level === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'
                              }`}>
                                {threat.level.toUpperCase()}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">{threat.sourceCity}, {threat.sourceCountry}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-white">{threat.confidence}%</div>
                            <div className="text-xs text-gray-500">confidence</div>
                          </div>
                        </div>

                        {(threat.attribution || threat.isCloud || threat.isTor || threat.isKnownScanner) && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {threat.attribution && (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                                {threat.attribution}
                              </span>
                            )}
                            {threat.isTor && (
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">
                                Tor Exit
                              </span>
                            )}
                            {threat.isCloud && (
                              <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded">
                                {threat.cloudProvider || 'Cloud'}
                              </span>
                            )}
                            {threat.isKnownScanner && (
                              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                                {threat.scannerName || 'Scanner'}
                              </span>
                            )}
                          </div>
                        )}

                        {threat.sgisScore !== undefined && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-gray-500">SGIS Threat Score</span>
                              <span className={`font-semibold ${
                                threat.sgisScore >= 75 ? 'text-red-400' :
                                threat.sgisScore >= 50 ? 'text-orange-400' :
                                threat.sgisScore >= 25 ? 'text-yellow-400' : 'text-gray-400'
                              }`}>{threat.sgisScore}/100</span>
                            </div>
                            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  threat.sgisScore >= 75 ? 'bg-red-500' :
                                  threat.sgisScore >= 50 ? 'bg-orange-500' :
                                  threat.sgisScore >= 25 ? 'bg-yellow-500' : 'bg-gray-600'
                                }`}
                                style={{ width: `${threat.sgisScore}%` }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="mt-2 pt-2 border-t border-sg-border flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            {threat.requestCount} requests • {threat.techniques.length} techniques
                          </div>
                          <a 
                            href={`https://www.abuseipdb.com/check/${threat.sourceIp}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Check
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: HONEYPOT PERFORMANCE - ROI Metrics */}
          {activeTab === 'honeypots' && (
            <div className="space-y-8">
              <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-cyan-400" />
                  Honeypot Effectiveness by Variant
                </h3>
                <p className="text-sm text-gray-400 mb-6">
                  Ranked by time-burn rate — the reliable per-variant effectiveness signal.
                  {' '}{ENGAGEMENT_VOCAB.timeBurned.hint}
                </p>
                <p className="text-xs text-gray-500 mb-6">
                  Deep callbacks are not broken out per variant here: they live in the hop
                  table, which this list API cannot join to a decoy variant. Use the
                  {' '}{ENGAGEMENT_VOCAB.deepCallbacks.label} total on the Overview tab for the
                  canonical figure.
                </p>

                {honeypotStats.length === 0 ? (
                  <div className="text-sm text-gray-500 bg-sg-dark rounded-lg p-8 border border-sg-border text-center">
                    No honeypot data yet. Deceptions will appear here once served to attackers.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {honeypotStats.slice(0, 10).map((hp) => (
                      <div key={hp.variant} className="bg-sg-dark rounded-lg p-4 border border-sg-border">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3 flex-wrap">
                            <code className="text-sm font-mono text-white">{hp.variant}</code>
                            <span
                              className={`px-2 py-0.5 text-xs rounded font-semibold ${
                              hp.timeBurnRate >= 50 ? 'bg-green-500/20 text-green-400' :
                              hp.timeBurnRate >= 25 ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}
                              title={ENGAGEMENT_VOCAB.timeBurned.hint}
                            >
                              {hp.timeBurnRate.toFixed(1)}% {ENGAGEMENT_VOCAB.timeBurned.short.toLowerCase()}
                            </span>
                            {hp.legacyEngaged > 0 && (
                              <span
                                className="px-2 py-0.5 text-xs rounded font-semibold bg-gray-500/15 text-gray-400"
                                title="Legacy breadcrumb-report flag. Not the canonical deep-callback metric — see the Deep Callbacks total above, which is hop-based."
                              >
                                {hp.legacyEngagedRate.toFixed(1)}% legacy engaged
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {Math.round(hp.avgTimeWasted / 1000)}s avg delay
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                          <div>
                            <div className="text-gray-500">{ENGAGEMENT_VOCAB.decoysServed.label}</div>
                            <div className="text-white font-semibold">{hp.served}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">{ENGAGEMENT_VOCAB.timeBurned.label}</div>
                            <div className="text-white font-semibold">{hp.timeBurned}</div>
                          </div>
                          <div title="Legacy breadcrumb-report flag, kept for continuity. Per-variant deep callbacks need a hop join the deceptions list API does not expose yet.">
                            <div className="text-gray-500">Legacy engaged</div>
                            <div className="text-gray-400 font-semibold">{hp.legacyEngaged}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">{ENGAGEMENT_VOCAB.timeWasted.label}</div>
                            <div className="text-white font-semibold">{Math.round(hp.timeWasted / 1000)}s</div>
                          </div>
                        </div>

                        {/* Progress bar = time-burn rate */}
                        <div className="mt-3">
                          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${
                                hp.timeBurnRate >= 50 ? 'bg-green-500' :
                                hp.timeBurnRate >= 25 ? 'bg-yellow-500' :
                                'bg-gray-600'
                              }`}
                              style={{ width: `${Math.min(hp.timeBurnRate, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: ATTACK TECHNIQUES - Deep Dive */}
          {activeTab === 'techniques' && (
            <div className="space-y-8">
              <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-400" />
                  MITRE ATT&CK Technique Analysis
                </h3>
                <p className="text-sm text-gray-400 mb-6">
                  Detailed breakdown of attack techniques observed in your environment, mapped to the MITRE ATT&CK framework
                </p>

                {trends.topTechniques && trends.topTechniques.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {trends.topTechniques.map((tech: any, idx: number) => (
                      <div key={tech.technique} className="bg-sg-dark rounded-lg p-4 border border-sg-border">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold text-sm">
                              #{idx + 1}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-white">{tech.technique}</div>
                              <div className="text-xs text-gray-500">{tech.count} occurrences</div>
                            </div>
                          </div>
                          <div className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded font-semibold">
                            {tech.count}
                          </div>
                        </div>
                        
                        {/* Visual bar */}
                        <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-600 to-purple-400"
                            style={{ width: `${Math.min(100, (tech.count / trends.topTechniques[0].count) * 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 bg-sg-dark rounded-lg p-8 border border-sg-border text-center">
                    No attack techniques detected yet. Data will appear here once threats are analyzed.
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
